import * as analytics from '@actions/analytics';
import { getCurrentHost } from '@actions/formManipulation';
import { cleanStorage, toLocalStorage } from '@actions/formulary';
import { formSteps, type StepKey } from '@config/formSteps';
import { UNCOVERED_DOMAIN_ID } from '@config/lists';
import { catalog } from '@core/catalog';
import { FormEvent, useEffect, useState } from 'react';
import { save, type LeadPayload } from '../../api';
import Error from '../Error';
import Thanks from '../Thanks';
import { useFormContext } from './context';
import { FormLayout } from './FormLayout/FormLayout';
import StepElement from './Steps/StepElement/StepElement';
import StepLayout from './Steps/StepLayout';
import Submitting from './Submitting';
import { useFormNavigation } from './useFormNavigation';

type SubmitStatus =
  | 'editing'
  | 'submitting'
  | 'success'
  | 'noResponse'
  | 'error';

const Form = () => {
  const { formDataContext, updateFormData } = useFormContext();

  const { current, previous, next, progressPercent, goToStep } =
    useFormNavigation(formDataContext);

  const [status, setStatus] = useState<SubmitStatus>('editing');

  useEffect(() => {
    if (status === 'success') {
      window.location.replace(
        `https://${window.location.hostname}/remerciement/`,
      );
    }
  }, [status]);

  const handleInvalid = (event: FormEvent<HTMLFormElement>) => {
    try {
      console.error('form is invalid it will not trigger', event);
      event.preventDefault();
      const invalidElements = (
        event.target as HTMLFormElement
      ).querySelectorAll(':invalid');

      invalidElements.forEach((element) => {
        const field = element as HTMLInputElement;
        console.error(
          `Validation error in ${field.name || field.id}: ${field.validationMessage}`,
        );
      });
    } catch (error) {
      console.log('error in handleInvalid', error);
    }
  };

  const persistLeadId = (leadId: string | number) => {
    if (leadId && leadId !== localStorage.getItem('lastLeadId')) {
      toLocalStorage({ lastLeadId: leadId });
      document.cookie = `leadId=${leadId}`;
    }
  };

  const submitLead = async () => {
    const dataForForm = {
      ...formDataContext,
      ...catalog().tracking,
      iss: getCurrentHost(),
      aud: 'addLead',
    } as LeadPayload;

    // La conversion est lancée au submit ; elle est ensuite "confirmée" via `conversion`.
    analytics.sendData({ event: 'ConversionTracking' });
    if (dataForForm.hasRequestedAdvice === 1) {
      analytics.sendData({ souhaiteEtreConseille: true });
    }

    const result = await save(dataForForm);
    if (!result.success) {
      analytics.sendData({ event: 'conversionFailed' });
      setStatus('error');
      return;
    }

    const leadId =
      result.data.id ??
      (result.data.result ? JSON.parse(result.data.result) : null);
    if (leadId) {
      analytics.sendData({ event: 'setLeadId', leadId });
      persistLeadId(leadId);
    }
    analytics.sendData({ event: 'conversion' });
    cleanStorage();
    setStatus(leadId ? 'success' : 'noResponse');
  };

  const handlePrevStep = () => {
    if (!previous) {
      return window.history.back();
    }
    analytics.sendData({ event: 'prevStep' });
    goToStep(previous);
  };

  const advanceTo = (key: StepKey) => {
    analytics.sendData({ event: 'nextStep' });
    goToStep(key);
  };

  const handleFormData = (formData: { name: string; value: unknown }) => {
    try {
      analytics.sendData(
        analytics.formatTrackingEvent({
          formData: formData as { name: string; value: string | number },
        }),
      );
    } catch (error) {
      console.error('erreur pendant gestion du tracking', error, formData);
    }

    updateFormData(formData);
  };

  // Vérifie que toutes les infos obligatoires sont présentes avant submit.
  const checkFullForm = () =>
    formDataContext.situationPro &&
    (formDataContext.situationPro !== '388' ||
      formDataContext.professionalSituationName) &&
    formDataContext.sourceId &&
    (formDataContext.sourceId !== '4180' || formDataContext.sourceName) &&
    (formDataContext.domainId !== UNCOVERED_DOMAIN_ID ||
      formDataContext.domainName) &&
    formDataContext.email &&
    formDataContext.nom &&
    formDataContext.prenom &&
    formDataContext.numInt;

  const handleSubmit = (event?: FormEvent<HTMLFormElement>) => {
    if (event) event.preventDefault();
    toLocalStorage(formDataContext);
    if (checkFullForm()) {
      setStatus('submitting');
      submitLead();
    }
  };

  if (status === 'error') {
    return <Error />;
  }

  if (status === 'noResponse') {
    return <Thanks />;
  }

  if (status === 'submitting' || status === 'success') {
    return <Submitting />;
  }

  return (
    <FormLayout progressPercent={progressPercent}>
      <form
        onSubmit={handleSubmit}
        onInvalid={handleInvalid}
        style={{ width: '100%' }}
      >
        <StepLayout previous={previous} handlePrevStep={handlePrevStep}>
          {current && (
            <StepElement
              key={current}
              stepData={formSteps[current]}
              formDataContext={formDataContext}
              handleFormData={handleFormData}
              advanceTo={advanceTo}
              next={next}
            />
          )}
        </StepLayout>
      </form>
    </FormLayout>
  );
};

export default Form;
